import { configureStore } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosMiddleware from 'redux-axios-middleware';
import thunk from 'redux-thunk';
import promise from 'redux-promise-middleware';
import reducer from './reducers';
import { handleSessionExpired } from './actions/user';
import config from './constants/config';
import endpoints from './constants/endpoints';
import { getAppAuthTokens, getAppSsoTokens, saveSsoTokens } from './utils/tokens';
import { getAppObj } from './utils/utils';

// updated 2

const windowExist = typeof window !== 'undefined';

const client = axios.create({
  baseURL: config.getApiUrl(),
  responseType: 'json'
});

if (windowExist) getAppObj().axiosDefaults = client.defaults;

const store = configureStore({
  reducer,
  middleware: [promise, axiosMiddleware(client), thunk]
});

export default store;

client.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const token = config.headers['x-auth-token'];
  const refresh = config.headers['x-refresh-token'];

  if (refresh) {
    delete config.headers['x-auth-token'];
    return config;
  }

  if (!token) {
    config.headers['x-auth-token'] = getAppAuthTokens().token || getAppSsoTokens().token;
  }

  return config;
});

const excludedUrls = [
  endpoints.me,
  endpoints.refresh,
  endpoints.authenticate,
  endpoints.passwordReset
];

// Tente un refresh force du token SSO (minValidity -1). Sert de filet de securite
// lorsque le token a expire entre deux refresh proactifs (onTokenExpired).
async function refreshSsoTokenReactively() {
  const { keycloak } = getAppObj();

  if (!keycloak?.authenticated) return false;

  try {
    await keycloak.updateToken(-1);
    saveSsoTokens();
    return true;
  } catch (e) {
    return false;
  }
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const statusCode = error?.response?.status;
    const failedConfig = error?.response?.config;

    if ((statusCode === 403 || statusCode === 401) && failedConfig) {
      const url = failedConfig.url.split(/[?#]/)[0];
      const isExcluded = excludedUrls.includes(url);

      if (!isExcluded) {
        // On tente un refresh reactif puis on rejoue la requete une seule fois
        // avant de considerer la session comme expiree.
        if (!failedConfig._tokenRetried) {
          failedConfig._tokenRetried = true;

          if (await refreshSsoTokenReactively()) {
            delete failedConfig.headers['x-auth-token'];
            return client(failedConfig);
          }
        }

        store.dispatch(handleSessionExpired());
      }
    }
    return Promise.reject(error);
  }
);
